import { Chart, ArcElement, Tooltip, Legend } from "chart.js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./OnBoardingAudit/NotoSans-Regular-normal";
import {
  apiRequestBodyBuilder,
  getCurrencySymbol,
  getLocalCurrencyStringType,
} from "./config/tableHelper";

Chart.register(ArcElement, Tooltip, Legend);

// Extended color palette for charts with many slices
const chartColors = [
  "#4e73df",
  "#1cc88a",
  "#36b9cc",
  "#f6c23e",
  "#e74a3b",
  "#858796",
  "#f8b425",
  "#5a5c69",
  "#2e59d9",
  "#17a673",
];

// Helper to generate dynamic text based on page type
const getReportStrings = (PageType, previousDates) => {
  const isMonthly = PageType === "monthly_reporting";
  const isWeekly = PageType === "weekly_reporting";
  const isDaily = PageType === "daily_reporting";

  const title = isMonthly
    ? "Monthly Performance Report"
    : isWeekly
      ? "Weekly Performance Report"
      : isDaily
        ? "Daily Performance Report"
        : "Performance Report";

  const summary = isMonthly
    ? "Summary of account performance for last month."
    : isWeekly
      ? "Summary of account performance for last week."
      : isDaily
        ? "Summary of account performance for yesterday."
        : "Summary of account performance for the selected period.";

  const dateStr =
    previousDates?.curr_end_date &&
    previousDates?.curr_start_date !== previousDates?.curr_end_date
      ? `${previousDates.curr_start_date} to ${previousDates.curr_end_date}`
      : previousDates?.curr_start_date || "";

  return { title, summary, dateStr };
};

export const handleCustomRepSharing = async ({
  previousDates,
  tableNames = ["device", "bidStrategy", "genderInfo", "audiences", "group"], // Updated Customizable defaults
  // group_name = ["group1", "group2"], // Added to support new API fields
  // insight_type = ["device", "audience", "gender", "group"], // Added to support new API fields
  metrics = { audiences: "impressions", genderInfo: "clicks", device: "cost" }, //not getting this from custom reporting...
  PageType = "custom_reporting",
  setLoading,
  downloadOnly = false,
}) => {
  if (setLoading) setLoading(true);

  let insight_type = [];

  // gender to genderInfo
  let newTableNames = tableNames.map((item) => {
    if (item == "gender") {
      item = "genderInfo";
    }
    return item;
  });
  try {
    const storedInsights = JSON.parse(
      localStorage.getItem("customrep_insight") || "{}",
    );
    // Only include the ones currently set to true
    insight_type = Object.keys(storedInsights).filter(
      (key) => storedInsights[key] === true,
    );
  } catch (error) {
    // insight_type = ["device", "audience", "gender", "group"];
    console.error("Failed to parse customrep_insight from localStorage", error);
  }

  let {
    title: reportTitle,
    summary: reportSummary,
    dateStr,
  } = getReportStrings(PageType, previousDates);
  if (PageType === "custom_reporting") {
    reportTitle = "Custom Performance Report";
    reportSummary =
      "Custom reporting details for the selected metrics and period.";
  }

  // Fallbacks and retrieval from localStorage based on your system variables
  const projectId = localStorage.getItem("project_id") || "12";
  const email = localStorage.getItem("email") || "user@example.com";
  const adAccountId = localStorage.getItem("ad_account_id") || "32";
  const isProjectLogoVisible = localStorage.getItem(
    `showLogoProject_${projectId}`,
  );

  let projectIconPrefixed = null;
  let responseData = [];
  let insightsData = [];

  try {
    const payload = {
      project_id: parseInt(projectId, 10),
      email: email,
      ad_account_id: parseInt(adAccountId, 10),
      table_names: newTableNames,
      // group_name: group_name,
      insight_type: insight_type,
      show_project_icon:
        isProjectLogoVisible === "true" || isProjectLogoVisible === true,
      metrics: metrics,
      start_date: previousDates?.curr_start_date || "2026-01-01",
      end_date: previousDates?.curr_end_date || "2026-07-21",
    };

    const res = await fetch(
      "https://api.confidanto.com/add-table-custom-reporting-data",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );

    const jsonResponse = await res.json();
    const iconBase64 = jsonResponse.project_icon;

    if (iconBase64) {
      const rawBase64 = `data:image/png;base64,${iconBase64}`;

      projectIconPrefixed = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          // --- CHANGE SIZE HERE ---
          const displaySize = 160; // Try 80, 100, 120, etc.
          const crispMultiplier = 2; // Renders at 2x quality for Retina screens

          const maxW = displaySize * crispMultiplier;
          const maxH = displaySize * crispMultiplier;

          let { width, height } = img;

          // Constrain dimensions while preserving aspect ratio
          if (width > maxW || height > maxH) {
            const ratio = Math.min(maxW / width, maxH / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }

          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");

          // Force highest quality anti-aliasing to stop the blur
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = "high";

          ctx.drawImage(img, 0, 0, width, height);

          // Pass the displaySize along with the Base64 string so the HTML knows what size to be
          resolve({ src: canvas.toDataURL("image/png"), size: displaySize });
        };
        img.onerror = () => resolve({ src: rawBase64, size: 80 });
        img.src = rawBase64;
      });
    }

    responseData = jsonResponse.results || [];
    insightsData = jsonResponse.insights || [];
  } catch (err) {
    console.error("Failed to fetch custom reporting data:", err);
  }

  // REVISED STYLING: Email clients break on Flexbox/Grid, so we use Tables.
  // The CSS is cleaned up to work in tandem with HTML table structures.
  const reportStyleTag = `
    <style>
      .rp-wrap {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        color: #0f172a;
        background: #f8fafc;
        line-height: 1.6;
      }
      .rp-container { width: 100%; margin: 0; padding: 20px 0; box-sizing: border-box; }
      .rp-header { border: 1px solid #e2e8f0; border-radius: 12px; padding: 24px; background: #ffffff; margin-bottom: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
      .rp-title { margin: 0; font-size: 26px; font-weight: 800; color: #1e293b; letter-spacing: -0.025em; }
      .rp-meta { margin-top: 4px; font-size: 14px; color: #64748b; }
      .rp-pill { display: inline-block; padding: 6px 14px; border-radius: 8px; background: #f1f5f9; color: #1e293b; font-weight: 600; font-size: 13px; }
      .rp-section { margin-top: 20px; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; background: #ffffff; }
      .rp-sectionHd { padding: 16px 20px; background: #ffffff; border-bottom: 1px solid #e2e8f0; }
      .rp-h2 { margin: 0; font-size: 20px; font-weight: 700; color: #1e293b; text-transform: uppercase; letter-spacing: 0.05em; }
      .rp-sectionBody { padding: 20px; }
      .rp-kicker { margin: 28px 0 12px 0; font-size: 18px; font-weight: 700; color: #2563eb; border-left: 4px solid #2563eb; padding-left: 12px; }
      .rp-table { width: 100%; border-collapse: collapse; margin-bottom: 10px; border: 1px solid #e2e8f0; }
      .rp-table th { background-color: #2930a8 !important; color: #ffffff !important; text-align: center; font-size: 12px; font-weight: 600; padding: 14px 10px; text-transform: uppercase; }
      .rp-table td { font-size: 13px; padding: 12px 10px; border-bottom: 1px solid #e2e8f0; text-align: center; color: #0f172a; }
      .rp-table tr:nth-child(even) td { background: #f8fafc; }
      .rp-total td { font-weight: 700; background: #f1f5f9 !important; }
      
      /* Email safe chart container overrides */
      .rp-grid { display: block; width: 100%; } 
      .rp-chart { border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px; background: #fff; margin-bottom: 20px; }
      .rp-chartTitle { margin-bottom: 15px; font-size: 16px; font-weight: 700; color: #1e293b; }
      
      /* Email safe Insights styling */
      .rp-insight-box { background-color: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 24px; margin-top: 10px; margin-bottom: 24px; }
      .rp-insight-label { width: 20%; vertical-align: top; padding-right: 15px; font-weight: 700; color: #2930a8; font-size: 13px; }
      .rp-insight-text { width: 80%; vertical-align: top; color: #475569; font-size: 13px; line-height: 1.6; }
      
      @media (max-width: 780px) { 
        /* Fallbacks for mobile screens */
        .rp-chartBodyTable, .rp-chartBodyTable tbody, .rp-chartBodyTable tr, .rp-chartBodyTable td { 
          display: block !important; 
          width: 100% !important; 
        }
        .rp-chartImgTd { 
          padding-right: 0 !important; 
          margin-bottom: 24px !important; 
          text-align: center !important; 
        }
        .rp-insight-label, .rp-insight-text {
          display: block !important;
          width: 100% !important;
        }
        .rp-insight-label { margin-bottom: 6px !important; }
      }
    </style>
  `.trim();

  const formatKeyLabel = (key) => {
    if (!key) return "";
    return key
      .replace(/_/g, " ")
      .replace(/\bctr\b/gi, "CTR")
      .replace(/\binteraction rate\b/gi, "Interaction Rate")
      .replace(/\baverage cpc\b/gi, "Average CPC")
      .replace(/\bCost per Conv\b/gi, "Cost/Conv")
      .replace(/\bcost per conv\b/gi, "Cost/Conv")
      .replace(/\bconversion rate\b/gi, "Conversion Rate")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getFormattedValue = (key, value, currency = "INR") => { // use default currency functoin from table helper later
    if (value == null) return "";
    const symbol = currency === "USD" ? "$" : "₹";
    if (
      key &&
      [
        "cost",
        "costs",
        "average_cpc",
        "cost_per_conversion",
        "cost_per_conv",
        "spend",
        "total_budget",
        "remaining_budget",
        "yesterday_spend",
      ].includes(key.toLowerCase())
    ) {
      return `${symbol} ${Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    if (key && ["ctr", "interaction_rate", "conversion_rate"].includes(key)) {
      return `${parseFloat(value).toFixed(2)}%`;
    }
    if (typeof value === "number") return Number(value).toLocaleString("en-IN");
    return value;
  };

  const renderSimpleTable = (dataArray, totals, currency = "INR") => {
    if (!Array.isArray(dataArray) || dataArray.length === 0)
      return "<p>No data available</p>";
    let allKeys = [
      ...new Set(dataArray.flatMap((obj) => Object.keys(obj))),
    ].filter((k) => k.toLowerCase() !== "campaign_ids");

    const primaryKeyOrder = [
      "group_name",
      "campaign_name",
      "device",
      "gender",
      "age_range",
      "campaign_type",
    ];
    allKeys.sort((a, b) => {
      const aIndex = primaryKeyOrder.indexOf(a),
        bIndex = primaryKeyOrder.indexOf(b);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      const desiredOrder = [
        "impressions",
        "clicks",
        "ctr",
        "costs",
        "cost",
        "average_cpc",
        "conversions",
        "cost_per_conversion",
        "conversion_rate",
      ];
      const aD = desiredOrder.indexOf(a),
        bD = desiredOrder.indexOf(b);
      if (aD !== -1 && bD !== -1) return aD - bD;
      return a.localeCompare(b);
    });

    const headerRow = allKeys
      .map((key) => `<th>${formatKeyLabel(key)}</th>`)
      .join("");
    const rows = dataArray
      .map(
        (row) =>
          `<tr>${allKeys.map((key) => `<td>${getFormattedValue(key, row[key], row.currency || currency)}</td>`).join("")}</tr>`,
      )
      .join("");
    let totalRowHtml = totals
      ? `<tr class="rp-total">${allKeys.map((key) => (primaryKeyOrder.includes(key) ? `<td>Total</td>` : `<td>${getFormattedValue(key, totals[key], currency)}</td>`)).join("")}</tr>`
      : "";

    return `<table class="rp-table"><thead><tr>${headerRow}</tr></thead><tbody>${rows}${totalRowHtml}</tbody></table>`;
  };

  const renderInsights = (tableName, insights) => {
    if (!insights || insights.length === 0) return "";

    // Normalize table names to match insight types returned by the API
    let targetInsightType = tableName.toLowerCase();
    if (targetInsightType === "audiences") targetInsightType = "audience";
    if (targetInsightType === "genderinfo") targetInsightType = "gender";

    const insightObj = insights.find(
      (i) => i.insight_type.toLowerCase() === targetInsightType,
    );

    if (!insightObj || !insightObj.data || insightObj.data.length === 0) {
      return "";
    }

    const rowsHtml = insightObj.data
      .map(
        (item) => `
      <table width="100%" cellpadding="0" cellspacing="0" border="0">
        <tr>
          <td class="rp-insight-label">${item.entity_name}:</td>
          <td class="rp-insight-text">${item.insight_text}</td>
        </tr>
      </table>
    `,
      )
      .join("<div style='height: 16px; width: 100%;'></div>");

    return `<div class="rp-insight-box">${rowsHtml}</div>`;
  };

  // 1. Generate Custom Charts
  let chartsHtml = "";
  for (const table of responseData) {
    if (table.success === false || !table.data || table.data.length === 0)
      continue;

    const metric = metrics[table.name];
    if (!metric) continue;

    const chartData = Array.isArray(table.data) ? table.data : [table.data];
    const firstItem = chartData[0];
    const labelKey = Object.keys(firstItem).find(
      (k) => typeof firstItem[k] === "string" && k.toLowerCase() !== "currency",
    );

    const values = chartData.map((item) => {
      const v = parseFloat(item[metric]) || 0;
      return v > 1 ? v : v * 100;
    });
    const labels = chartData.map((item) => formatKeyLabel(item[labelKey]));

    const canvas = document.createElement("canvas");
    canvas.width = 300;
    canvas.height = 300;
    const ctx = canvas.getContext("2d");

    const chartColors = [
      "#3b82f6",
      "#10b981",
      "#f59e0b",
      "#ef4444",
      "#8b5cf6",
      "#6366f1",
      "#14b8a6",
      "#ec4899",
      "#06b6d4",
      "#f97316",
    ];

    await new Promise((resolve) => {
      new Chart(ctx, {
        type: "doughnut",
        data: {
          labels,
          datasets: [
            {
              data: values,
              backgroundColor: chartColors.slice(0, values.length),
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: false,
          animation: { onComplete: () => resolve() },
          plugins: { legend: { display: false } },
        },
      });
    });

    // EMAIL-SAFE LEGEND ITEMS (Nested Tables inside Divs)
    const legendListHtml = labels
      .map((label, i) => {
        const val = getFormattedValue(
          metric,
          values[i],
          chartData[i]?.currency || "INR",
        );
        const color = chartColors[i % chartColors.length];
        return `
        <div style="margin-bottom: 8px;">
          <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px;">
            <tr>
              <td style="padding: 10px 14px; font-size: 13px; color: #334155; text-align: left; vertical-align: middle;">
                <span style="color: ${color}; font-size: 16px; margin-right: 2px;">●</span>&nbsp;&nbsp;${label}
              </td>
              <td style="padding: 10px 14px; font-size: 13px; color: #0f172a; font-weight: 700; text-align: right; vertical-align: middle;">
                ${val}
              </td>
            </tr>
          </table>
        </div>
      `;
      })
      .join("");

    // EMAIL-SAFE CHART LAYOUT (Table format instead of display: flex)
    chartsHtml += `
      <div class="rp-chart">
        <div class="rp-chartTitle">${formatKeyLabel(table.name)} (${metric.toUpperCase()})</div>
        
        <table class="rp-chartBodyTable" width="100%" cellpadding="0" cellspacing="0" border="0">
          <tr>
            <td class="rp-chartImgTd" width="45%" align="center" valign="middle" style="padding-right: 24px; text-align: center;">
              <img src="${canvas.toDataURL("image/png")}" width="250" style="width: 100%; max-width: 250px; display: inline-block; height: auto;" alt="Chart" />
            </td>
            <td class="rp-chartLegendTd" width="55%" valign="middle">
              ${legendListHtml}
            </td>
          </tr>
        </table>

      </div>
    `;
  }

  // 2. Generate Custom Detailed Tables
  let additionalTablesHtml = responseData
    .map((table) => {
      if (table.success === false || !table.data) return "";
      const tableData = Array.isArray(table.data) ? table.data : [table.data];

      const tableHtml = renderSimpleTable(tableData, table.totals);
      const insightHtml = renderInsights(table.name, insightsData);

      return `
        <div class="rp-kicker">${formatKeyLabel(table.name)}</div>
        ${tableHtml}
        ${insightHtml}
      `;
    })
    .join("");

  // 3. Assemble Complete HTML Document
  const projectIconHtml = projectIconPrefixed
    ? `<img src="${projectIconPrefixed.src}" alt="Project Logo" width="${projectIconPrefixed.size}" style="display: block; width: ${projectIconPrefixed.size}px; max-width: ${projectIconPrefixed.size}px; height: auto; object-fit: contain;">`
    : "";

  const fullHtml = `
    ${reportStyleTag}
    <div class="rp-wrap">
      <div class="rp-container">
        <div class="rp-header">
          <table width="100%" cellpadding="0" cellspacing="0" border="0" style="width: 100%; border-collapse: collapse;">
            <tr>
              <td width="30%" align="left" valign="middle">${projectIconHtml}</td>
              <td width="40%" align="center" valign="middle" style="text-align: center;">
                <h1 class="rp-title">${reportTitle}</h1>
                <div class="rp-meta">${reportSummary}</div>
              </td>
              <td width="30%" align="right" valign="middle">
                <div class="rp-pill">${dateStr}</div>
              </td>
            </tr>
          </table>
        </div>
        <div class="rp-section">
          <div class="rp-sectionHd"><div class="rp-h2">Custom Visualizations</div></div>
          <div class="rp-sectionBody"><div class="rp-grid">${chartsHtml || "<p>No visual data available.</p>"}</div></div>
        </div>
        <div class="rp-section">
          <div class="rp-sectionHd"><div class="rp-h2">Detailed Breakdown Tables</div></div>
          <div class="rp-sectionBody">${additionalTablesHtml || "<p>No tabular data available.</p>"}</div>
        </div>
      </div>
    </div>
  `.trim();

  if (downloadOnly) {
    if (setLoading) setLoading(false);
    return fullHtml;
  }

  // Define UI Icons (Cloned for independence)
  const Icons = {
    copy: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>`,
    mail: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>`,
    success: `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#10b981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>`,
    error: `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>`,
    info: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink: 0; margin-top: 2px;"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>`,
    close: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`,
    spinner: `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="animation: spin 1s linear infinite;"><line x1="12" y1="2" x2="12" y2="6"></line><line x1="12" y1="18" x2="12" y2="22"></line><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line><line x1="2" y1="12" x2="6" y2="12"></line><line x1="18" y1="12" x2="22" y2="12"></line><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line><style>@keyframes spin { 100% { transform: rotate(360deg); } }</style></svg>`,
  };

  // Launch the familiar modal sequence
  await new Promise((resolve) => {
    const overlay = document.createElement("div");
    Object.assign(overlay.style, {
      position: "fixed",
      top: "0",
      left: "0",
      width: "100vw",
      height: "100vh",
      backgroundColor: "rgba(15, 23, 42, 0.4)",
      backdropFilter: "blur(4px)",
      WebkitBackdropFilter: "blur(4px)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: "99999",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      opacity: "0",
      transition: "opacity 0.2s ease-in-out",
    });

    const modal = document.createElement("div");
    Object.assign(modal.style, {
      background: "#ffffff",
      borderRadius: "16px",
      boxShadow:
        "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
      width: "440px",
      maxWidth: "92%",
      overflow: "hidden",
      transform: "scale(0.95)",
      transition: "transform 0.2s ease-in-out",
    });

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    requestAnimationFrame(() => {
      overlay.style.opacity = "1";
      modal.style.transform = "scale(1)";
    });

    const closeModal = () => {
      overlay.style.opacity = "0";
      modal.style.transform = "scale(0.95)";
      setTimeout(() => {
        if (document.body.contains(overlay)) document.body.removeChild(overlay);
        resolve();
      }, 200);
    };

    const modalStyles = `
      <style>
        .custom-mod-btn { display: flex; align-items: center; justify-content: center; gap: 8px; padding: 12px 16px; border: none; border-radius: 8px; font-weight: 600; font-size: 14px; cursor: pointer; transition: all 0.15s ease; box-sizing: border-box; }
        .custom-mod-btn-primary { background: #2563eb; color: #fff; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
        .custom-mod-btn-primary:hover { background: #1d4ed8; }
        .custom-mod-btn-secondary { background: #f1f5f9; color: #334155; }
        .custom-mod-btn-secondary:hover { background: #e2e8f0; }
        .custom-mod-input:focus { border-color: #3b82f6 !important; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important; outline: none; }
        .custom-mod-close:hover { background: #f1f5f9; color: #0f172a !important; }
      </style>
    `;

    const renderInitial = () => {
      modal.innerHTML = `
        ${modalStyles}
        <div style="padding: 24px; position: relative;">
          <button id="btn-close-top" class="custom-mod-close" style="position: absolute; top: 16px; right: 16px; background: transparent; border: none; cursor: pointer; color: #94a3b8; padding: 6px; border-radius: 6px; display: flex; align-items: center; justify-content: center; transition: all 0.15s ease;">
            ${Icons.close}
          </button>
          <div style="text-align: center;">
            <h3 style="margin: 0 0 8px 0; color: #0f172a; font-size: 20px; font-weight: 700;">Share Custom Report</h3>
            <p style="color: #64748b; font-size: 14px; margin: 0 0 28px 0; line-height: 1.5;">Choose how you want to share this custom performance report.</p>
            
            <div style="display: flex; flex-direction: column; gap: 12px;">
              <button id="btn-copy" class="custom-mod-btn custom-mod-btn-secondary" style="width: 100%; border: 1px solid #e2e8f0;">
                ${Icons.copy} Copy HTML to Clipboard
              </button>
              <button id="btn-email" class="custom-mod-btn custom-mod-btn-primary" style="width: 100%;">
                ${Icons.mail} Send via Email
              </button>
            </div>
          </div>
        </div>
      `;

      document.getElementById("btn-close-top").onclick = closeModal;

      document.getElementById("btn-copy").onclick = async () => {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({
              "text/html": new Blob([fullHtml], { type: "text/html" }),
              "text/plain": new Blob([fullHtml.replace(/<[^>]+>/g, "")], {
                type: "text/plain",
              }),
            }),
          ]);
          modal.innerHTML = `
            <div style="padding: 40px 24px; text-align: center;">
              <div style="display: flex; justify-content: center; margin-bottom: 16px;">${Icons.success}</div>
              <h3 style="color: #0f172a; margin: 0 0 8px 0; font-size: 20px;">Copied!</h3>
              <p style="color: #64748b; font-size: 14px; margin: 0;">The report has been copied to your clipboard.</p>
            </div>
          `;
          setTimeout(closeModal, 1800);
        } catch (err) {
          modal.innerHTML = `
            <div style="padding: 40px 24px; text-align: center;">
              <div style="display: flex; justify-content: center; margin-bottom: 16px;">${Icons.error}</div>
              <h3 style="color: #0f172a; margin: 0 0 8px 0; font-size: 20px;">Copy Failed</h3>
              <p style="color: #64748b; font-size: 14px; margin: 0;">Please check your browser permissions.</p>
            </div>
          `;
          setTimeout(closeModal, 2000);
        }
      };

      document.getElementById("btn-email").onclick = renderEmailInput;
    };

    const renderEmailInput = () => {
      modal.innerHTML = `
        ${modalStyles}
        <div style="padding: 24px;">
          <h3 style="margin: 0 0 8px 0; color: #0f172a; font-size: 20px; font-weight: 700;">Send via Email</h3>
          <p style="color: #64748b; font-size: 14px; margin: 0 0 20px 0; line-height: 1.5;">Enter the recipient's email address below.</p>
          
          <div style="margin-bottom: 24px;">
            <label style="display: block; text-align: left; font-size: 13px; font-weight: 600; color: #334155; margin-bottom: 6px;">Email Address</label>
            <input type="email" id="email-input" class="custom-mod-input" placeholder="client@example.com" style="width: 100%; padding: 12px 14px; border: 1px solid #cbd5e1; border-radius: 8px; box-sizing: border-box; font-size: 14px; outline: none; transition: all 0.2s; color: #0f172a;" />
          </div>

          <div style="display: flex; gap: 12px;">
            <button id="btn-cancel-email" class="custom-mod-btn custom-mod-btn-secondary" style="flex: 1;">Back</button>
            <button id="btn-send" class="custom-mod-btn custom-mod-btn-primary" style="flex: 1;">Send Report</button>
          </div>
        </div>
      `;

      const emailInput = document.getElementById("email-input");
      setTimeout(() => emailInput.focus(), 50);

      document.getElementById("btn-cancel-email").onclick = renderInitial;
      emailInput.addEventListener("keydown", (e) => {
        if (e.key === "Enter") document.getElementById("btn-send").click();
      });

      document.getElementById("btn-send").onclick = async () => {
        const targetEmail = emailInput.value.trim();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!targetEmail || !emailRegex.test(targetEmail)) {
          emailInput.style.borderColor = "#ef4444";
          emailInput.style.boxShadow = "0 0 0 3px rgba(239, 68, 68, 0.1)";
          return;
        }

        modal.innerHTML = `
          <div style="padding: 48px 24px; text-align: center;">
            <div style="display: flex; justify-content: center; margin-bottom: 20px;">${Icons.spinner}</div>
            <h3 style="color: #0f172a; margin: 0 0 8px 0; font-size: 18px;">Sending Report...</h3>
            <p style="color: #64748b; font-size: 14px; margin: 0;">Please wait a moment.</p>
          </div>
        `;

        try {
          const emailRes = await fetch(
            "https://api.confidanto.com/send-report",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                to: targetEmail,
                subject: `${reportTitle} - ${dateStr}`,
                html: fullHtml,
              }),
            },
          );
          const eData = await emailRes.json();

          if (eData.success) {
            modal.innerHTML = `
              ${modalStyles}
              <div style="padding: 24px;">
                <div style="display: flex; flex-direction: column; align-items: center; text-align: center; margin-bottom: 24px;">
                  <div style="margin-bottom: 16px;">${Icons.success}</div>
                  <h3 style="color: #0f172a; margin: 0 0 8px 0; font-size: 20px; font-weight: 700;">Report Sent Successfully!</h3>
                  <p style="color: #64748b; font-size: 14px; margin: 0;">The report was delivered to <strong>${targetEmail}</strong></p>
                </div>
                
                <div style="background: #f0fdf4; border: 1px solid #bbf7d0; padding: 12px 16px; border-radius: 8px; display: flex; align-items: flex-start; gap: 10px; margin-bottom: 24px;">
                  <div style="color: #16a34a;">${Icons.info}</div>
                  <div style="font-size: 13px; color: #166534; line-height: 1.5; text-align: left;">
                    <strong>Note:</strong> We have dispatched this report via our official system email address.
                  </div>
                </div>

                <button id="btn-close-final" class="custom-mod-btn custom-mod-btn-secondary" style="width: 100%;">Close Window</button>
              </div>
            `;
          } else {
            modal.innerHTML = `
              ${modalStyles}
              <div style="padding: 24px; text-align: center;">
                <div style="display: flex; justify-content: center; margin-bottom: 16px;">${Icons.error}</div>
                <h3 style="color: #0f172a; margin: 0 0 8px 0; font-size: 20px;">Delivery Failed</h3>
                <p style="color: #64748b; font-size: 14px; margin: 0 0 24px 0;">We could not send the email at this time. Please try again later.</p>
                <button id="btn-close-final" class="custom-mod-btn custom-mod-btn-secondary" style="width: 100%;">Close Window</button>
              </div>
            `;
          }
          document.getElementById("btn-close-final").onclick = closeModal;
        } catch (err) {
          modal.innerHTML = `
            ${modalStyles}
            <div style="padding: 24px; text-align: center;">
              <div style="display: flex; justify-content: center; margin-bottom: 16px;">${Icons.error}</div>
              <h3 style="color: #0f172a; margin: 0 0 8px 0; font-size: 20px;">Network Error</h3>
              <p style="color: #64748b; font-size: 14px; margin: 0 0 24px 0;">An unexpected error occurred while sending.</p>
              <button id="btn-close-final" class="custom-mod-btn custom-mod-btn-secondary" style="width: 100%;">Close Window</button>
            </div>
          `;
          document.getElementById("btn-close-final").onclick = closeModal;
        }
      };
    };

    renderInitial();
  });

  if (setLoading) setLoading(false);
};
